<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useStore } from "vuex";
import { getRefferId } from "@/common/cookies";
import {
  getGoogleAvatarFromJWT,
  getGoogleUserInfoFromJWT,
  getAppleUserInfo,
} from "@/common/utils";

const props = defineProps({
  mode: {
    type: String,
    default: "signin", // signin or signup
  },
});

const emit = defineEmits(["success", "error"]);

const toast = useToast();
const store = useStore();

// 响应式数据
const googleReady = ref(false);
const googleBtnRef = ref(null);

// 生命周期钩子
onMounted(() => {
  initAppleLogin();
  initGoogleLogin();
});

onBeforeUnmount(() => {
  // 清理事件监听
  document.removeEventListener("AppleIDSignInOnSuccess", appleSuccessCallback);
  document.removeEventListener("AppleIDSignInOnFailure", appleFailCallback);
});

// 方法定义
const initAppleLogin = () => {
  let retryCount = 0;
  const maxRetries = 100; // 最多重试100次，总共10秒

  // 检查 Apple ID 是否已加载
  const checkAppleID = () => {
    if (window.AppleID && window.AppleID.auth) {
      const AppleID = window.AppleID;

      // 构建回调URL：当前域名 + /apple-callback
      const currentOrigin = window.location.origin;
      const appleCallbackUrl = `${currentOrigin}/apple-callback/`;

      try {
        AppleID.auth.init({
          clientId: "com.sharkfoto-login",
          scope: "openid name email",
          redirectURI: appleCallbackUrl,
          state: "openid,name,email",
          usePopup: true, // 改为false，使用重定向模式
        });

        // 添加事件监听
        document.addEventListener(
          "AppleIDSignInOnSuccess",
          appleSuccessCallback
        );
        document.addEventListener("AppleIDSignInOnFailure", appleFailCallback);

        console.log("Apple ID 初始化成功");
      } catch (error) {
        console.error("Apple ID 初始化失败:", error);
      }
    } else if (retryCount < maxRetries) {
      // 如果 Apple ID 未加载且未超过最大重试次数，延迟重试
      retryCount++;
      setTimeout(checkAppleID, 100);
    } else {
      // 超过最大重试次数，显示错误信息
      console.error("Apple ID 脚本加载超时，请检查网络连接或刷新页面重试");
    }
  };

  // 开始检查
  checkAppleID();
};

const initGoogleLogin = () => {
  const searchGoogle = setInterval(() => {
    if (window.google) {
      clearInterval(searchGoogle);
      googleReady.value = true;
      nextTick(() => {
        // 初始化并渲染隐藏的 Google 按钮
        const google = window.google;
        google.accounts.id.initialize({
          client_id:
            "395815285672-tvtam5eudp775lcqkpheanevmr93roao.apps.googleusercontent.com",
          callback: handleCredentialResponse,
        });

        // 渲染隐藏的按钮
        if (googleBtnRef.value) {
          google.accounts.id.renderButton(googleBtnRef.value, {
            theme: "outline",
            size: "large",
            width: 300,
          });
        }
      });
    }
  }, 300);
};

const modifyUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.search = "";
    parsedUrl.pathname = parsedUrl.pathname.endsWith("/")
      ? parsedUrl.pathname
      : parsedUrl.pathname + "/";
    return parsedUrl.toString();
  } catch (e) {
    console.error("Invalid URL", e);
    return url;
  }
};

// 手动触发第三方登录
const signUpWith = async (provider) => {
  if (provider === "google") {
    if (!googleReady.value) {
      toast.add({
        title: "Error",
        description: "Google login is not ready yet, please try again",
        color: "error",
      });
      return;
    }
    // 触发隐藏的 Google 按钮点击
    if (googleBtnRef.value) {
      const googleBtn = googleBtnRef.value.querySelector('div[role="button"]');
      if (googleBtn) {
        googleBtn.click();
      } else {
        console.error("Google button not found in hidden element");
        toast.add({
          title: "Error",
          description: "Google login button not ready",
          color: "error",
        });
      }
    }
  } else if (provider === "apple") {
    if (!window.AppleID || !window.AppleID.auth) {
      toast.add({
        title: "Error",
        description: "Apple login is not ready yet, please try again",
        color: "error",
      });
      return;
    }
    // 保存当前页面URL到sessionStorage，用于登录成功后重定向
    const currentUrl = window.location.href;
    sessionStorage.setItem("apple_login_redirect", currentUrl);

    // 触发 Apple 登录
    try {
      await window.AppleID.auth.signIn();
    } catch (error) {
      console.error("Apple login error:", error);
      // 清除保存的redirect
      sessionStorage.removeItem("apple_login_redirect");
      if (error !== "popup_closed_by_user") {
        toast.add({
          title: "Error",
          description: "Apple login failed",
          color: "error",
        });
      }
    }
  }
};

const appleSuccessCallback = async (payload) => {
  console.log("Apple login params:", payload);
  try {
    const { code, id_token, state } = payload.detail.authorization;
    const user = payload.detail.user;
    const params = {
      code,
      id_token,
      state,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (user) {
      params.user = user;
    }

    // 提取Apple用户信息（注意：Apple不提供头像URL）
    const appleUserInfo = getAppleUserInfo(payload);
    console.log("Apple用户信息:", appleUserInfo);
    if (appleUserInfo) {
      console.log("Apple邮箱:", appleUserInfo.email);
      console.log("Apple名称:", appleUserInfo.name);
      console.log("Apple头像:", appleUserInfo.picture); // 始终为null，因为Apple不提供
    }

    // 添加 referral_id
    const refferId = getRefferId();
    console.log("从cookie获取的refferId", refferId);
    if (refferId) {
      params.referral_id = refferId;
    }
    console.log("params", params);

    const result = await store.dispatch("user/loginWithApple", params);
    handleLoginCallBack(result);
  } catch (err) {
    console.error("Apple login error:", err);
    toast.add({
      title: "Error",
      description: "Apple login failed",
      color: "error",
    });
    emit("error", err);
  }
};

const appleFailCallback = (event) => {
  const result = event.detail.error;
  if (result === "popup_closed_by_user") {
    console.log("User closed Apple login popup");
  }
};

const handleLoginCallBack = (result) => {
  // 登录完成之后的处理
  if (result.status === 200) {
    // 保存登录模式到 localstorage
    localStorage.setItem("login_mode", "thirdPart");
    console.log(
      "登录模式保存到localStorage",
      localStorage.getItem("login_mode")
    );

    // 触发成功事件，让父组件关闭弹窗
    emit("success", result);
  } else {
    if (result.status === 410 && result.message === "InvalidEmailAddress") {
      toast.add({
        title: "Error",
        description: "Email address is invalid",
        color: "error",
      });
    } else if (result.status === 412 && result.message === "InvalidPassword") {
      toast.add({
        title: "Error",
        description: "Password is incorrect",
        color: "error",
      });
    } else {
      toast.add({
        title: "Error",
        description: result.message || "Login failed",
        color: "error",
      });
    }
    emit("error", result);
  }
};

const handleCredentialResponse = async (response) => {
  const jwt = response.credential;

  // 提取Google用户信息和头像
  const googleAvatarUrl = getGoogleAvatarFromJWT(jwt);
  const googleUserInfo = getGoogleUserInfoFromJWT(jwt);

  console.log("Google头像URL:", googleAvatarUrl);
  console.log("Google用户信息:", googleUserInfo);
  if (googleUserInfo) {
    console.log("Google用户名:", googleUserInfo.name);
    console.log("Google邮箱:", googleUserInfo.email);
    console.log("Google头像:", googleUserInfo.picture);
  }

  const postData = {
    token: jwt,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  // 添加 referral_id
  const refferId = getRefferId();
  console.log("从cookie获取的refferId", refferId);
  if (refferId) {
    postData.referral_id = refferId;
  }
  console.log("postData", postData);
  // 请求登录
  try {
    const result = await store.dispatch("user/loginWithGoogle", postData);
    handleLoginCallBack(result);
  } catch (err) {
    console.error("Google login error:", err);
    toast.add({
      title: "Error",
      description: "Google login failed",
      color: "error",
    });
    emit("error", err);
  }
};

// 设置页面头部
useHead({
  script: [
    {
      src: "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
      type: "text/javascript",
      defer: "true",
    },
    {
      src: "https://accounts.google.com/gsi/client",
      type: "text/javascript",
      defer: "true",
    },
  ],
});
</script>

<template>
  <div
    class="third-party-login-buttons flex flex-col items-center justify-center"
  >
    <p class="text-sm text-muted-foreground mb-3 text-center">
      {{ mode === "signin" ? "Sign in with:" : "Sign up with:" }}
    </p>

    <!-- 社交登录按钮 -->
    <div class="flex flex-col gap-3 w-full">
      <UButton
        color="white"
        variant="solid"
        size="lg"
        class="rounded-lg px-8 h-12 bg-white text-black border border-gray-200 hover:bg-gray-100 w-full justify-center text-center"
        icon="logos:google-icon"
        :label="
          mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'
        "
        @click="signUpWith('google')"
      />
      <UButton
        color="white"
        variant="solid"
        size="lg"
        class="rounded-lg px-8 h-12 bg-white text-black border border-gray-200 hover:bg-gray-100 w-full justify-center text-center"
        icon="logos:apple"
        :label="mode === 'signin' ? 'Sign in with Apple' : 'Sign up with Apple'"
        @click="signUpWith('apple')"
      />
    </div>

    <!-- 隐藏的 Google 原生按钮 -->
    <div ref="googleBtnRef" class="hidden-google-btn"></div>
  </div>
</template>

<style scoped>
.third-party-login-buttons {
  width: 100%;
}

/* 隐藏 Google 原生按钮 */
.hidden-google-btn {
  position: absolute;
  left: -9999px;
  visibility: hidden;
  pointer-events: none;
}
</style>
